import React, { useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import store from '@/redux/store';
import { buildTheme } from '@/theme';
import { ColorModeContext } from '@/contexts/ColorModeContext';
import { setColorMode } from '@/redux/slices/uiSlice';
import { ConfirmProvider } from '@/components/common/ConfirmProvider';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import App from './App';

// Enable dayjs relative-time formatting (used by format.fromNow).
dayjs.extend(relativeTime);

// Root wires the color-mode context + theme to the ui slice, then the global providers.
function Root() {
  const dispatch = useDispatch();
  const mode = useSelector((s) => s.ui.colorMode);
  const theme = useMemo(() => buildTheme(mode), [mode]);
  const colorMode = useMemo(
    () => ({ mode, toggle: () => dispatch(setColorMode(mode === 'light' ? 'dark' : 'light')) }),
    [mode, dispatch],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} autoHideDuration={3500}>
          <ConfirmProvider>
            <ErrorBoundary>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ErrorBoundary>
          </ConfirmProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Provider store={store}>
      <Root />
    </Provider>
  </React.StrictMode>,
);
